import { audioContext } from '../audio/engine.js';
import { haptic } from '../lib/haptics.js';

/**
 * Sound effects + haptic feedback for lesson interactions.
 *
 * Sounds are synthesized on-the-fly via the Web Audio API — no MP3s to
 * ship, no autoplay quirks (one user gesture is enough to unlock the
 * AudioContext), and they scale with the device's media volume.
 *
 * If the user mutes via Profile → "ხმოვანი ეფექტები" the volume goes
 * to 0 but the events still fire (so haptic + analytics keep working).
 *
 * Replaceable with designer-supplied MP3s post-launch — just swap the
 * `play*` impls without touching the call sites.
 */

export type SfxKind = 'correct' | 'wrong' | 'complete' | 'tap' | 'star_lit';

const MUTE_KEY = 'fluent.sound-muted.v1';

export function isSoundMuted(): boolean {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem(MUTE_KEY) === '1';
}

export function setSoundMuted(muted: boolean): void {
  if (typeof window === 'undefined') return;
  if (muted) localStorage.setItem(MUTE_KEY, '1');
  else localStorage.removeItem(MUTE_KEY);
}

interface Note {
  freq: number;
  duration: number;
  /** ms from sequence start. */
  start?: number;
  /** Wave type — sine is the warmest, square the harshest. */
  type?: OscillatorType;
  /** Peak gain. Each kind keeps gain < 0.25 so we don't blow ear-drums. */
  gain?: number;
}

function playSequence(notes: Note[]): void {
  if (isSoundMuted()) return;
  // The voice engine's context: one context for all sound, so it shares the
  // engine's iOS recovery (a context iOS left 'interrupted' is replaced on
  // the next tap — see audio/engine.ts).
  const ac = audioContext();
  if (!ac) return;
  if (ac.state !== 'running') {
    // Suspended until a user gesture, or 'interrupted' (iOS); resume opportunistically.
    ac.resume().catch(() => {});
  }
  const now = ac.currentTime;
  for (const n of notes) {
    const osc = ac.createOscillator();
    const g = ac.createGain();
    osc.type = n.type ?? 'sine';
    osc.frequency.value = n.freq;
    const startAt = now + (n.start ?? 0) / 1000;
    const stopAt = startAt + n.duration / 1000;
    const peak = n.gain ?? 0.18;
    // Tiny attack + exponential release — keeps clicks out, sounds soft.
    g.gain.setValueAtTime(0.0001, startAt);
    g.gain.exponentialRampToValueAtTime(peak, startAt + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, stopAt);
    osc.connect(g).connect(ac.destination);
    osc.start(startAt);
    osc.stop(stopAt + 0.02);
  }
}

function playCorrect(): void {
  // Pleasant rising third (E5 → G5) — universally read as "yes"
  playSequence([
    { freq: 659.25, duration: 90 },
    { freq: 783.99, duration: 140, start: 80 },
  ]);
}

function playWrong(): void {
  // Soft thud — low triangle wave, quick decay. Not punishing, just clear.
  playSequence([
    { freq: 220.0, duration: 160, type: 'triangle', gain: 0.22 },
    { freq: 174.61, duration: 220, type: 'triangle', gain: 0.22, start: 60 },
  ]);
}

function playComplete(): void {
  // Mini-fanfare: C5 → E5 → G5 → C6 (major arpeggio up).
  playSequence([
    { freq: 523.25, duration: 110 },
    { freq: 659.25, duration: 110, start: 100 },
    { freq: 783.99, duration: 110, start: 200 },
    { freq: 1046.5, duration: 220, start: 300, gain: 0.22 },
  ]);
}

function playTap(): void {
  // Tiny click — very short, very high-frequency, very quiet.
  playSequence([{ freq: 1200, duration: 22, type: 'square', gain: 0.05 }]);
}

function playStarLit(): void {
  // Skill-tree star light-up: bright jewel sparkle. Three high sine
  // notes ascending fast (C7 → E7 → G7) — feels like a chime.
  playSequence([
    { freq: 1046.5, duration: 70, type: 'sine', gain: 0.18 },
    { freq: 1318.5, duration: 80, type: 'sine', gain: 0.18, start: 50 },
    { freq: 1568.0, duration: 140, type: 'sine', gain: 0.2, start: 110 },
  ]);
}

export function playSfx(kind: SfxKind): void {
  if (typeof window === 'undefined') return;
  // Always fire the event — tests + future analytics rely on it.
  try {
    window.dispatchEvent(
      new CustomEvent<{ kind: SfxKind }>('fluent:sfx', { detail: { kind } }),
    );
  } catch {
    /* noop */
  }
  // Haptic always fires (it's brief, doesn't depend on volume).
  haptic(kind);
  // Sound respects the mute flag.
  switch (kind) {
    case 'correct':
      return playCorrect();
    case 'wrong':
      return playWrong();
    case 'complete':
      return playComplete();
    case 'tap':
      return playTap();
    case 'star_lit':
      return playStarLit();
  }
}
